import * as React from "react"
import { createContext, use, useMemo } from "react"

import { cn } from "../../lib/utils"

export type PixelGridIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8
export type PixelGridMotion = "auto" | "static"
export type PixelGridSpeed = "slow" | "normal" | "fast"
export type PixelGridTone = "primary" | "neutral" | "info" | "success" | "warning" | "danger"
export type PixelGridSize = "xs" | "sm" | "md" | "lg" | number
export type PixelGridGap = "tight" | "normal" | "loose" | number
export type PixelGridShape = "square" | "round"
export type PixelGridCellState = "pattern" | "on" | "off"

export type PixelGridDelays = readonly [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
]

export type PixelGridColors = readonly [
  string?,
  string?,
  string?,
  string?,
  string?,
  string?,
  string?,
  string?,
  string?,
]

export interface PixelGridPattern {
  name?: string
  delays: PixelGridDelays
  duration?: number
  colors?: PixelGridColors
}

/**
 * Delay presets inspired by the original 3-pixel-grid project.
 *
 * Each delay is ordered left-to-right, top-to-bottom and maps to one of the
 * nine cells in the grid.
 */
export const PIXEL_GRID_PRESETS = {
  "wave-lr": {
    name: "wave-lr",
    delays: [0, 120, 240, 0, 120, 240, 0, 120, 240],
    duration: 220,
  },
  "wave-rl": {
    name: "wave-rl",
    delays: [240, 120, 0, 240, 120, 0, 240, 120, 0],
    duration: 220,
  },
  "wave-tb": {
    name: "wave-tb",
    delays: [0, 0, 0, 120, 120, 120, 240, 240, 240],
    duration: 220,
  },
  "wave-bt": {
    name: "wave-bt",
    delays: [240, 240, 240, 120, 120, 120, 0, 0, 0],
    duration: 220,
  },
  "spiral-cw": {
    name: "spiral-cw",
    delays: [0, 80, 160, 560, 640, 240, 480, 400, 320],
    duration: 200,
  },
  "corners-first": {
    name: "corners-first",
    delays: [0, 200, 0, 200, 400, 200, 0, 200, 0],
    duration: 220,
  },
  "center-out": {
    name: "center-out",
    delays: [240, 120, 240, 120, 0, 120, 240, 120, 240],
    duration: 220,
  },
  "diagonal-tl": {
    name: "diagonal-tl",
    delays: [0, 100, 200, 100, 200, 300, 200, 300, 400],
    duration: 200,
  },
  snake: {
    name: "snake",
    delays: [0, 80, 160, 400, 320, 240, 480, 560, 640],
    duration: 180,
  },
  cross: {
    name: "cross",
    delays: [300, 0, 300, 0, 0, 0, 300, 0, 300],
    duration: 260,
  },
  checkerboard: {
    name: "checkerboard",
    delays: [0, 250, 0, 250, 0, 250, 0, 250, 0],
    duration: 240,
  },
  rain: {
    name: "rain",
    delays: [0, 180, 60, 120, 300, 240, 360, 80, 420],
    duration: 190,
  },
  pinwheel: {
    name: "pinwheel",
    delays: [0, 160, 480, 320, 640, 160, 480, 320, 0],
    duration: 170,
  },
  orbit: {
    name: "orbit",
    delays: [0, 80, 160, 480, 640, 240, 400, 320, 560],
    duration: 150,
  },
  converge: {
    name: "converge",
    delays: [0, 160, 80, 240, 320, 240, 80, 160, 0],
    duration: 280,
  },
  zigzag: {
    name: "zigzag",
    delays: [0, 160, 320, 400, 240, 80, 480, 560, 640],
    duration: 170,
  },
  pulse: {
    name: "pulse",
    delays: [0, 0, 0, 0, 0, 0, 0, 0, 0],
    duration: 280,
  },
  "center-in": {
    name: "center-in",
    delays: [0, 120, 0, 120, 240, 120, 0, 120, 0],
    duration: 220,
  },
  "diagonal-br": {
    name: "diagonal-br",
    delays: [400, 300, 200, 300, 200, 100, 200, 100, 0],
    duration: 200,
  },
  frame: {
    name: "frame",
    delays: [0, 80, 160, 80, 360, 240, 160, 240, 320],
    duration: 220,
  },
  ripple: {
    name: "ripple",
    delays: [0, 120, 240, 120, 0, 120, 240, 120, 0],
    duration: 260,
  },
  bounce: {
    name: "bounce",
    delays: [0, 160, 320, 480, 320, 160, 0, 160, 320],
    duration: 180,
  },
  aurora: {
    name: "aurora",
    delays: [0, 100, 200, 100, 200, 300, 200, 300, 400],
    duration: 240,
    colors: ["cyan", "cyan", "teal", "teal", "#4f8cff", "#4f8cff", "#9b7bff", "#9b7bff", "#f084c8"],
  },
  ember: {
    name: "ember",
    delays: [0, 80, 160, 560, 640, 240, 480, 400, 320],
    duration: 200,
    colors: ["#f4d35e", "#f49d37", "#f49d37", "#f49d37", "#ef6f6c", "#ef6f6c", "#ef6f6c", "#d94f9d", "#d94f9d"],
  },
  brand: {
    name: "brand",
    delays: [0, 80, 160, 560, 640, 240, 480, 400, 320],
    duration: 200,
    colors: [
      "var(--primary-300, var(--primary))",
      "var(--primary-400, var(--primary))",
      "var(--primary-400, var(--primary))",
      "var(--primary-500, var(--primary))",
      "var(--primary-500, var(--primary))",
      "var(--primary-600, var(--primary))",
      "var(--primary-600, var(--primary))",
      "var(--primary-700, var(--primary))",
      "var(--primary-700, var(--primary))",
    ],
  },
  prism: {
    name: "prism",
    delays: [0, 80, 160, 240, 320, 400, 480, 560, 640],
    duration: 180,
    colors: ["#e76f51", "#f4a261", "#e9c46a", "#72b878", "#4bb3a7", "#4f8cff", "#8c72d9", "#d06ab1", "#e989b8"],
  },
  "neon-cross": {
    name: "neon-cross",
    delays: [300, 0, 300, 0, 0, 0, 300, 0, 300],
    duration: 260,
    colors: ["#e57bd4", "#5bc0eb", "#e57bd4", "#5bc0eb", "#f8f6f0", "#5bc0eb", "#e57bd4", "#5bc0eb", "#e57bd4"],
  },
  tide: {
    name: "tide",
    delays: [0, 0, 0, 120, 120, 120, 240, 240, 240],
    duration: 220,
    colors: ["#5db9a8", "#5bc0eb", "#5db9a8", "#4f8cff", "#5db9a8", "#4f8cff", "#8c72d9", "#4f8cff", "#8c72d9"],
  },
  sunset: {
    name: "sunset",
    delays: [240, 240, 240, 120, 120, 120, 0, 0, 0],
    duration: 220,
    colors: ["#8c72d9", "#4f8cff", "#8c72d9", "#d06ab1", "#ef6f6c", "#d06ab1", "#f49d37", "#f4d35e", "#f49d37"],
  },
  toxic: {
    name: "toxic",
    delays: [0, 200, 0, 200, 400, 200, 0, 200, 0],
    duration: 220,
    colors: ["#a9d64b", "#62b56b", "#a9d64b", "#62b56b", "#e9c46a", "#62b56b", "#a9d64b", "#62b56b", "#a9d64b"],
  },
  frost: {
    name: "frost",
    delays: [240, 120, 240, 120, 0, 120, 240, 120, 240],
    duration: 220,
    colors: [
      "oklch(90% 24% 83deg)",
      "oklch(77% 43% 64deg)",
      "oklch(69% 52% 41deg)",
      "oklch(77% 43% 64deg)",
      "oklch(69% 52% 41deg)",
      "oklch(77% 43% 64deg)",
      "oklch(69% 52% 41deg)",
      "oklch(77% 43% 64deg)",
      "oklch(90% 24% 83deg)",
    ],
  },
} as const satisfies Record<string, PixelGridPattern>

export type PixelGridPresetName = keyof typeof PIXEL_GRID_PRESETS
export type PixelGridPatternInput = PixelGridPresetName | PixelGridPattern

const CELL_INDICES = [0, 1, 2, 3, 4, 5, 6, 7, 8] as const
const DEFAULT_PATTERN_NAME: PixelGridPresetName = "wave-lr"
const DEFAULT_DURATION = 220
const FADE_DURATION = 320

const SIZE_VALUES: Record<Exclude<PixelGridSize, number>, number> = {
  xs: 3,
  sm: 4,
  md: 5,
  lg: 7,
}

const GAP_VALUES: Record<Exclude<PixelGridGap, number>, number> = {
  tight: 1,
  normal: 2,
  loose: 3,
}

const SPEED_FACTORS: Record<PixelGridSpeed, number> = {
  slow: 0.72,
  normal: 1,
  fast: 1.3,
}

const TONE_TOKENS: Record<PixelGridTone, { on: string; off: string; glow: string }> = {
  primary: {
    on: "var(--primary)",
    off: "color-mix(in oklch, var(--primary) 24%, transparent)",
    glow: "color-mix(in oklch, var(--primary) 58%, transparent)",
  },
  neutral: {
    on: "var(--muted-foreground)",
    off: "color-mix(in oklch, var(--muted-foreground) 24%, transparent)",
    glow: "color-mix(in oklch, var(--muted-foreground) 48%, transparent)",
  },
  info: {
    on: "var(--status-info-1)",
    off: "color-mix(in oklch, var(--status-info-1) 24%, transparent)",
    glow: "color-mix(in oklch, var(--status-info-1) 58%, transparent)",
  },
  success: {
    on: "var(--status-success-1)",
    off: "color-mix(in oklch, var(--status-success-1) 24%, transparent)",
    glow: "color-mix(in oklch, var(--status-success-1) 58%, transparent)",
  },
  warning: {
    on: "var(--status-warning-1)",
    off: "color-mix(in oklch, var(--status-warning-1) 24%, transparent)",
    glow: "color-mix(in oklch, var(--status-warning-1) 58%, transparent)",
  },
  danger: {
    on: "var(--status-danger-1)",
    off: "color-mix(in oklch, var(--status-danger-1) 24%, transparent)",
    glow: "color-mix(in oklch, var(--status-danger-1) 58%, transparent)",
  },
}

type ResolvedPixelGridPattern = {
  name: string
  delays: PixelGridDelays
  duration: number
  colors?: PixelGridColors
}

type PixelGridStyle = React.CSSProperties & Record<string, string | number | undefined>

interface PixelGridContextValue {
  pattern: ResolvedPixelGridPattern
  speedFactor: number
  color?: string
}

const PixelGridContext = createContext<PixelGridContextValue | null>(null)

function usePixelGridContext() {
  const context = use(PixelGridContext)

  if (!context) {
    throw new Error("PixelGrid.Cell must be rendered inside <PixelGrid.Root>.")
  }

  return context
}

function isPixelGridPresetName(value: string): value is PixelGridPresetName {
  return Object.prototype.hasOwnProperty.call(PIXEL_GRID_PRESETS, value)
}

function resolvePattern(pattern: PixelGridPatternInput = DEFAULT_PATTERN_NAME): ResolvedPixelGridPattern {
  const candidate: PixelGridPattern =
    typeof pattern === "string"
      ? (isPixelGridPresetName(pattern)
          ? PIXEL_GRID_PRESETS[pattern]
          : PIXEL_GRID_PRESETS[DEFAULT_PATTERN_NAME])
      : pattern

  const normalizeDelay = (delay: number) => (Number.isFinite(delay) ? delay : 0)
  const delays: PixelGridDelays = [
    normalizeDelay(candidate.delays[0]),
    normalizeDelay(candidate.delays[1]),
    normalizeDelay(candidate.delays[2]),
    normalizeDelay(candidate.delays[3]),
    normalizeDelay(candidate.delays[4]),
    normalizeDelay(candidate.delays[5]),
    normalizeDelay(candidate.delays[6]),
    normalizeDelay(candidate.delays[7]),
    normalizeDelay(candidate.delays[8]),
  ]
  const duration = Number.isFinite(candidate.duration) ? Math.max(80, candidate.duration ?? DEFAULT_DURATION) : DEFAULT_DURATION

  return {
    name: candidate.name ?? "custom",
    delays,
    duration,
    colors: candidate.colors,
  }
}

function resolveSize(size: PixelGridSize) {
  if (typeof size === "number") return Math.max(1, size)
  return SIZE_VALUES[size]
}

function resolveGap(gap: PixelGridGap) {
  if (typeof gap === "number") return Math.max(0, gap)
  return GAP_VALUES[gap]
}

function resolveCycleDuration(pattern: ResolvedPixelGridPattern, speedFactor: number) {
  const maxDelay = Math.max(...pattern.delays)
  const cycleDuration = maxDelay + pattern.duration + FADE_DURATION
  return Math.max(FADE_DURATION, Math.round(cycleDuration / speedFactor))
}

function resolveToneTokens(tone: PixelGridTone, color?: string) {
  const trimmedColor = color?.trim()

  if (!trimmedColor) return TONE_TOKENS[tone]

  return {
    on: trimmedColor,
    off: `color-mix(in oklch, ${trimmedColor} 24%, transparent)`,
    glow: `color-mix(in oklch, ${trimmedColor} 58%, transparent)`,
  }
}

export interface PixelGridRootProps extends Omit<React.ComponentPropsWithRef<"span">, "children"> {
  children?: React.ReactNode
  pattern?: PixelGridPatternInput
  tone?: PixelGridTone
  color?: string
  size?: PixelGridSize
  gap?: PixelGridGap
  shape?: PixelGridShape
  speed?: PixelGridSpeed
  motion?: PixelGridMotion
}

function resolvePixelGridChildren(children: React.ReactNode) {
  const resolvedChildren = React.Children.toArray(children)

  if (countPixelGridCells(children) > CELL_INDICES.length) {
    throw new Error("PixelGrid.Root supports at most nine cells.")
  }

  return resolvedChildren
}

export function PixelGridRoot({
  children,
  pattern = DEFAULT_PATTERN_NAME,
  tone = "primary",
  color,
  size = "sm",
  gap = "normal",
  shape = "square",
  speed = "normal",
  motion = "auto",
  className,
  style,
  ref,
  role,
  "aria-label": ariaLabel,
  "aria-hidden": ariaHidden,
  ...rest
}: PixelGridRootProps) {
  const resolvedPattern = useMemo(() => resolvePattern(pattern), [pattern])
  const speedFactor = SPEED_FACTORS[speed]
  const cellSize = resolveSize(size)
  const cellGap = resolveGap(gap)
  const resolvedColor = color?.trim() || undefined
  const toneTokens = resolveToneTokens(tone, resolvedColor)
  const cycleDuration = resolveCycleDuration(resolvedPattern, speedFactor)

  const contextValue = useMemo<PixelGridContextValue>(
    () => ({ pattern: resolvedPattern, speedFactor, color: resolvedColor }),
    [resolvedColor, resolvedPattern, speedFactor],
  )

  const rootStyle: PixelGridStyle = {
    "--unitfield-pixel-grid-size": `${cellSize}px`,
    "--unitfield-pixel-grid-gap": `${cellGap}px`,
    "--unitfield-pixel-grid-cycle": `${cycleDuration}ms`,
    "--unitfield-pixel-grid-on": toneTokens.on,
    "--unitfield-pixel-grid-off": toneTokens.off,
    "--unitfield-pixel-grid-glow": toneTokens.glow,
    ...style,
  }

  const content =
    children == null
      ? CELL_INDICES.map((index) => <PixelGridCell key={index} index={index} />)
      : resolvePixelGridChildren(children)
  const defaultAriaHidden = ariaHidden ?? (ariaLabel || role ? undefined : true)

  return (
    <PixelGridContext.Provider value={contextValue}>
      <span
        ref={ref}
        role={role}
        aria-label={ariaLabel}
        aria-hidden={defaultAriaHidden}
        data-pixel-grid-motion={motion}
        data-pixel-grid-pattern={resolvedPattern.name}
        data-pixel-grid-shape={shape}
        data-pixel-grid-tone={tone}
        className={cn("unitfield-pixel-grid", className)}
        style={rootStyle}
        {...rest}
      >
        {content}
      </span>
    </PixelGridContext.Provider>
  )
}

export interface PixelGridCellProps
  extends Omit<React.ComponentPropsWithRef<"span">, "children" | "role" | keyof React.AriaAttributes> {
  index: number
  state?: PixelGridCellState
  color?: string
}

function stripPixelGridCellSemantics(props: object) {
  const sanitized: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(props)) {
    if (key === "role" || key.startsWith("aria-")) continue
    sanitized[key] = value
  }

  return sanitized
}

function countPixelGridCells(children: React.ReactNode): number {
  let count = 0

  React.Children.forEach(children, (child) => {
    if (!React.isValidElement<{ children?: React.ReactNode }>(child)) return

    if (child.type === PixelGridCell) {
      count += 1
      return
    }

    count += countPixelGridCells(child.props.children)
  })

  return count
}

export function PixelGridCell({
  index,
  state = "pattern",
  color,
  className,
  style,
  ref,
  ...rest
}: PixelGridCellProps) {
  const { color: rootColor, pattern, speedFactor } = usePixelGridContext()
  const normalizedIndex = CELL_INDICES[Math.min(8, Math.max(0, Math.round(index)))] ?? 0
  const cellColor = color ?? rootColor ?? pattern.colors?.[normalizedIndex]
  const cellStyle: PixelGridStyle = {
    "--unitfield-pixel-grid-delay": `${Math.round(pattern.delays[normalizedIndex] / speedFactor)}ms`,
    ...(cellColor
      ? {
          "--unitfield-pixel-grid-cell-on": cellColor,
          "--unitfield-pixel-grid-cell-off": `color-mix(in oklch, ${cellColor} 24%, transparent)`,
          "--unitfield-pixel-grid-cell-glow": `color-mix(in oklch, ${cellColor} 58%, transparent)`,
        }
      : {}),
    ...style,
  }

  return (
    <span
      {...stripPixelGridCellSemantics(rest)}
      ref={ref}
      aria-hidden="true"
      data-pixel-grid-cell=""
      data-pixel-grid-index={normalizedIndex}
      data-pixel-grid-state={state}
      className={cn("unitfield-pixel-grid__cell", className)}
      style={cellStyle}
    />
  )
}

export interface PixelGridLoaderProps
  extends Omit<PixelGridRootProps, "children" | "motion" | "role" | "aria-label" | "aria-hidden"> {
  label?: string
  decorative?: boolean
}

export function PixelGridLoader({ label = "Loading", decorative = false, ...props }: PixelGridLoaderProps) {
  return (
    <PixelGridRoot
      {...props}
      motion="auto"
      role={decorative ? undefined : "status"}
      aria-label={decorative ? undefined : label}
      aria-hidden={decorative ? true : undefined}
    />
  )
}

export type LoadingIndicatorPreset = "brand" | "compact"

export const LOADING_INDICATOR_PRESETS = {
  brand: {
    pattern: "frost" as const,
    size: "md" as const,
    tone: "neutral" as const,
  },
  compact: {
    pattern: "pulse" as const,
    size: "xs" as const,
    tone: "neutral" as const,
  },
} satisfies Record<LoadingIndicatorPreset, {
  pattern: PixelGridPresetName
  size: PixelGridSize
  tone: PixelGridTone
}>

export interface LoadingIndicatorProps
  extends Omit<PixelGridLoaderProps, "pattern" | "size" | "tone"> {
  /** Semantic preset: brand for page/section/component loading, compact for actions. */
  preset?: LoadingIndicatorPreset
  size?: PixelGridSize
  tone?: PixelGridTone
}

/** One status owner, with a continuous contour that draws and resolves into the mark. */
export function LoadingIndicator({
  preset = "brand",
  size,
  tone,
  color,
  label = "Loading",
  decorative = false,
  gap = "normal",
  speed = "normal",
  shape: _shape,
  className,
  style,
  ref,
  ...props
}: LoadingIndicatorProps) {
  const presetConfig = LOADING_INDICATOR_PRESETS[preset]
  const resolvedColor =
    preset === "compact" && color === undefined && tone === undefined ? "currentColor" : color
  // Preserve the footprint of existing callers, including page-sized indicators.
  const extent = size === undefined && preset === "compact"
    ? 18
    : resolveSize(size ?? presetConfig.size) * 3 + resolveGap(gap) * 2
  const markStyle: PixelGridStyle = {
    "--unitfield-loading-size": `${extent}px`,
    "--unitfield-loading-color": resolveToneTokens(tone ?? presetConfig.tone, resolvedColor).on,
    "--unitfield-loading-duration": `${3200 / SPEED_FACTORS[speed]}ms`,
    ...style,
  }

  return (
    <span
      {...props}
      ref={ref}
      role={decorative ? undefined : "status"}
      aria-label={decorative ? undefined : label}
      aria-hidden={decorative ? true : undefined}
      data-loading-indicator-preset={preset}
      className={cn("unitfield-logo-loading", className)}
      style={markStyle}
    >
      <svg className="size-full" viewBox={preset === "compact" ? "2 1 20 22" : "0 0 24 24"} fill="currentColor" aria-hidden="true" focusable="false">
        <path
          className="unitfield-logo-loading__silhouette"
          fillRule="evenodd"
          d="M3 8.25 21 2v20H3V8.25Zm4.5 3.2V17.5h9V8.45l-9 3Z"
        />
        <path
          className="unitfield-logo-loading__ink"
          fillRule="evenodd"
          d="M3 8.25 21 2v20H3V8.25Zm4.5 3.2V17.5h9V8.45l-9 3Z"
        />
        <path
          className="unitfield-logo-loading__contour unitfield-logo-loading__outer"
          pathLength="100"
          d="M3 22V8.25L21 2v20Z"
        />
        <path
          className="unitfield-logo-loading__contour unitfield-logo-loading__inner"
          pathLength="100"
          d="M7.5 17.5h9V8.45l-9 3Z"
        />
      </svg>
    </span>
  )
}

export interface LoadingRegionProps
  extends Omit<React.ComponentPropsWithRef<"div">, "aria-busy"> {
  /** The affected region owns aria-busy; the indicator owns role=status. */
  busy: boolean
  label?: string
  /** Supply a composed indicator when the region needs a custom reserved slot. */
  indicator?: React.ReactNode
}

/**
 * Marks an affected region without taking over its layout or router
 * ownership. Keep the host's reserved space in the region's own layout.
 */
export function LoadingRegion({
  busy,
  label = "Loading",
  indicator,
  children,
  className,
  ref,
  ...rest
}: LoadingRegionProps) {
  return (
    <div
      ref={ref}
      {...rest}
      data-loading-region=""
      data-loading-region-state={busy ? "busy" : "idle"}
      aria-busy={busy}
      className={className}
    >
      {busy ? indicator ?? <LoadingIndicator label={label} /> : null}
      {children}
    </div>
  )
}

export type PixelGridStaticProps = Omit<PixelGridRootProps, "children" | "motion">

export function PixelGridStatic(props: PixelGridStaticProps) {
  return <PixelGridRoot {...props} motion="static" />
}

export type PixelGridDotsProps = Omit<PixelGridRootProps, "shape">

export function PixelGridDots(props: PixelGridDotsProps) {
  return <PixelGridRoot {...props} shape="round" />
}

/**
 * A composable 3x3 pixel-grid primitive.
 *
 * The direct form renders the default nine cells. Use the compound members
 * when a surface needs to control individual cells or an explicit variant:
 *
 * ```tsx
 * <PixelGrid pattern="center-out" tone="success" />
 * <PixelGrid.Loader label="Loading properties" />
 * <PixelGrid.Static tone="neutral" />
 * <PixelGrid.Dots pattern="pulse" tone="info" />
 * <PixelGrid.Root motion="static">
 *   <PixelGrid.Cell index={0} state="on" />
 *   <PixelGrid.Cell index={4} state="on" />
 * </PixelGrid.Root>
 * ```
 */
export const PixelGrid = Object.assign(PixelGridRoot, {
  Root: PixelGridRoot,
  Cell: PixelGridCell,
  Dots: PixelGridDots,
  Loader: PixelGridLoader,
  Static: PixelGridStatic,
})
